import { Button, StyledText } from 'UI';
import { CourseDto } from 'api/generated';
import { useBundleUpsell } from 'hooks';
import { FC } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';

// Extend CourseDto to include bundleUpsellId property
type ExtendedCourseDto = CourseDto & {
  bundleUpsellId?: string;
};

type BundleCardProps = {
  bundle: ExtendedCourseDto; // Can be either a bundle or an individual course with bundle upsell
};

const BundleCard: FC<BundleCardProps> = ({ bundle }) => {
  const { colors } = useTheme();
  const { handleUpgradeBundle: handleUpgradeBundleFlow } = useBundleUpsell();

  // Enhanced bundle status detection with proper payment type differentiation
  const getBundleStatus = () => {
    if (!bundle.currentPayment) return 'Free Trial';

    const { type, level, status } = bundle.currentPayment;

    // Purchase = One-time payment, permanent access
    if (type === 'Purchase') {
      return 'Lifetime';
    }

    // Installment = Payment plan, ongoing payments
    if (type === 'Installment') {
      return 'Payment Plan';
    }

    // Subscription = Recurring payment with level
    if (type === 'Subscription' && status === 'active') {
      return level ? level.charAt(0).toUpperCase() + level.slice(1) : 'Active';
    }

    // If payment type is 'Free' or incomplete status, it's a trial
    if (type === 'Free' || status === 'incomplete' || status === 'incomplete_expired') {
      return 'Free Trial';
    }

    // Fallback for uncertain states
    return 'Trial';
  };

  const getStatusColor = () => {
    const status = getBundleStatus();

    if (status === 'Lifetime') {
      return colors.system.green;
    }

    if (status === 'Payment Plan') {
      return colors.system.blue; // Different color for payment plans
    }

    if (status === 'Trial' || status === 'Free Trial') {
      return colors.primary[3];
    }

    return colors.primary[1];
  };

  const handleUpgradeClick = async () => {
    await handleUpgradeBundleFlow(bundle);
  };

  const isTrialBundle = () => {
    const status = getBundleStatus();
    return status === 'Free Trial' || status === 'Trial';
  };

  // Determine if this is actually a bundle or individual course
  const isActualBundle = (bundle.type as string) === 'bundle';
  const displayLabel = isActualBundle ? 'Bundle' : 'Course';
  const buttonText = isActualBundle ? 'Upgrade Bundle' : 'Upgrade';

  return (
    <Root>
      <BundleHeader>
        <BundleIndicator $statusColor={getStatusColor()} />
        <BundleInfo>
          <StyledText font="body_large_semibold_16">{bundle.name}</StyledText>
          <StyledText
            font="body_basic_regular_14"
            customStyles={css`
              color: ${colors.neutrals[5]};
            `}>
            {displayLabel}
          </StyledText>
        </BundleInfo>
      </BundleHeader>

      <StatusContainer>
        <StatusBadge $statusColor={getStatusColor()}>
          <StyledText
            font="footnote_semibold_12"
            customStyles={css`
              color: ${colors.neutrals[11]};
            `}>
            {getBundleStatus()}
          </StyledText>
        </StatusBadge>
      </StatusContainer>

      {isTrialBundle() && (
        <UpgradeContainer>
          <UpgradeButton variant="primary" onClick={handleUpgradeClick}>
            {buttonText}
          </UpgradeButton>
        </UpgradeContainer>
      )}
    </Root>
  );
};

export default BundleCard;

const Root = styled.div`
  ${({ theme: { colors } }) => css`
    width: 100%;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    background-color: ${colors.neutrals[11]};
    border: 1px solid ${colors.primary[1]};
    border-radius: 12px;

    ${respondToWidth.sm`
      padding: 16px;
    `}
  `}
`;

const BundleHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const BundleIndicator = styled.div<{ $statusColor: string }>`
  ${({ $statusColor }) => css`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: ${$statusColor};
    flex-shrink: 0;
    margin-top: 2px;
  `}
`;

const BundleInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const StatusContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StatusBadge = styled.div<{ $statusColor: string }>`
  ${({ $statusColor }) => css`
    padding: 6px 12px;
    border-radius: 20px;
    background-color: ${$statusColor};
    display: flex;
    align-items: center;
    justify-content: center;
  `}
`;

const UpgradeContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 4px;
`;

const UpgradeButton = styled(Button)`
  ${({ theme: { colors } }) => css`
    width: 100%;
    max-width: 200px;
    font-size: 14px;
    padding: 8px 16px;

    &:hover {
      background-color: ${colors.primary[2]};
    }
  `}
`;
